import { useEffect, useState } from 'react';
import { useDownloadPdfFile } from 'src/components/selfService/features/OrderHistory/hooks/useDownloadPdfFile';
import { PDFGenerationStatus } from 'src/components/selfService/features/OrderHistory/OrderHistory.utils';
import { useGeneratePdfFile } from './useGeneratePdfFile';
import { useOrderReceiptPdfDetails } from './useOrderReceiptPdfDetails';

export interface UsePdfStatus {
  generatePdf: () => void;
  setStatus: (status: PDFGenerationStatus) => void;
  downloadUrl: string;
  status: PDFGenerationStatus;
}

export interface UsePdfStatusProps {
  corpId: string;
  orderId: string;
  filename: string;
}

export const usePdfStatus = ({
  corpId,
  orderId,
  filename,
}: UsePdfStatusProps): UsePdfStatus => {
  const { status: receiptDetailsPdfStatus, pdfUrl: receiptDetailsDownloadUrl } =
    useOrderReceiptPdfDetails({
      corpId,
      orderId,
    });

  const {
    generatePdf,
    downloadUrl: generateDownloadUrl,
    status: generatePdfStatus,
  } = useGeneratePdfFile({
    corpId,
    orderId,
  });
  const [status, setStatus] = useState(receiptDetailsPdfStatus);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const { getPDFFileFromS3 } = useDownloadPdfFile({
    downloadUrl,
    filename,
    onError,
  });

  useEffect(() => {
    if (receiptDetailsPdfStatus === PDFGenerationStatus.FETCHING) {
      generatePdf();
    }
    setStatus(receiptDetailsPdfStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptDetailsPdfStatus]);

  useEffect(() => {
    if (generatePdfStatus !== PDFGenerationStatus.IDLE) {
      setStatus(generatePdfStatus);
    }
  }, [generatePdfStatus]);

  useEffect(() => {
    if (status === PDFGenerationStatus.DOWNLOAD) {
      setDownloadUrl(receiptDetailsDownloadUrl || generateDownloadUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (generatePdfStatus === PDFGenerationStatus.DOWNLOAD && !!downloadUrl) {
      getPDFFileFromS3();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatePdfStatus, downloadUrl]);

  function onError() {
    setStatus(PDFGenerationStatus.ERROR);
  }

  return {
    generatePdf,
    downloadUrl,
    status,
    setStatus,
  };
};
